/**
 * Turns a keydown into a global-shortcut binding in the syntax the Rust shell
 * parses (`Ctrl+Shift+KeyM`, TechSpec §10.3). `code` rather than `key`, so the
 * binding names the physical key whatever the keyboard layout. Rust validates
 * again; this only gives immediate feedback.
 */

export type Capture =
  | { kind: 'binding'; binding: string }
  /** A bare key would swallow ordinary typing in every other application. */
  | { kind: 'needsModifier' }
  /** Esc without modifiers: stop capturing, change nothing. */
  | { kind: 'cancel' }
  /** Only a modifier so far, or Tab: keep waiting / let focus move. */
  | { kind: 'ignore' };

type KeyLike = Pick<KeyboardEvent, 'code' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'metaKey'>;

const MODIFIER_CODE = /^(Control|Shift|Alt|Meta|OS)(Left|Right)?$/;

export function captureBinding(event: KeyLike): Capture {
  const modifiers = [
    event.ctrlKey && 'Ctrl',
    event.altKey && 'Alt',
    event.shiftKey && 'Shift',
    event.metaKey && 'Super',
  ].filter((m): m is string => typeof m === 'string');

  if (event.code === '' || MODIFIER_CODE.test(event.code)) return { kind: 'ignore' };
  if (modifiers.length === 0) {
    if (event.code === 'Escape') return { kind: 'cancel' };
    if (event.code === 'Tab') return { kind: 'ignore' };
    return { kind: 'needsModifier' };
  }
  return { kind: 'binding', binding: [...modifiers, event.code].join('+') };
}
